import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useLayoutEffect,
  useCallback
} from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useStatsigClient } from '@statsig/react-bindings'
import { useSearchParams } from 'react-router-dom'
import { Tonality } from '@dsny/dsny-component-library'
import OptionActions from 'reducers/Options'
import useArchiveToFilter from 'utils/hooks/useArchiveToFilter'
import {
  createArchivedFilter,
  hasArchivedFilter,
  isArchivedFilter
} from 'utils/archiveFilter'
import InfiniteScroll from 'external/infiniteScroll/app'
import { ResponsiveLayout } from 'containers'
import { ReleasesMenu } from 'components'
import { RELEASE_QUICKVIEW_MODAL } from 'constants/featureFlags'
import { NotificationType } from 'constants/enums'
import { RESPONSIVE_BREAKPOINT_PAGES } from 'constants/responsiveBreakpoints'
import { PLAY_SOURCES, PLAY_TRIGGERS } from 'constants/tracking'
import {
  RESPONSIVE_BREAKPOINT_VERSIONS,
  ResponsiveBreakpointsProvider,
  useResponsiveBreakpointsVersion
} from 'contexts/ResponsiveBreakpointsContext'
import { thunks as DownloadsThunks } from 'reducers/Downloads'
import NotificationActions from 'reducers/Notification'
import { thunks as PlayQueueThunks } from 'reducers/PlayQueue'
import ReleaseActions, { thunks as ReleaseThunks } from 'reducers/Releases'
import { thunks as AuthThunks } from 'reducers/Auth'
import { ReleasesService } from 'services'
import useLocalStorage from 'utils/hooks/useLocalStorage'
import { useDesktopMediaQuery } from 'utils/hooks/useMediaQueries'
import { decodeToken } from 'utils/stringHelpers'
import ReleaseCard from 'components/ReleaseCard/ReleaseCard'
import ResponsiveBreakpointsRolloutBoundary from 'components/ResponsiveBreakpointsRolloutBoundary'
import RecentlySearched from '../Search/RecentlySearched'
import { ReleasesGrid } from './ReleasesView.styles'
import rvStyles from './ReleasesView.module.scss'
import EmptyReleasesFilterView from '../NoReleasesFilterView'
import ReleaseList from './ReleaseList/ReleaseList'
import ReleaseListSkeleton from './Skeleton/ReleaseListSkeleton'
import ReleaseCardSkeleton from './Skeleton/ReleaseCardSkeleton'
import { releasesSelector } from './selectors'

export const ReleasesContent = () => {
  const { t } = useTranslation()
  const { enabled: archiveToFilterEnabled, isLoading: isArchiveGateLoading } =
    useArchiveToFilter()
  const [searchParams, setSearchParams] = useSearchParams()
  const archivedRedirect = searchParams.get('archived') === 'true'
  const isDesktop = useDesktopMediaQuery()
  const responsiveBreakpointsVersion = useResponsiveBreakpointsVersion()
  const responsiveBreakpointsV2 =
    responsiveBreakpointsVersion === RESPONSIVE_BREAKPOINT_VERSIONS.V2
  const {
    releases,
    page,
    totalItemsCount,
    authLoading,
    isListView,
    scrollPosition
  } = useSelector(releasesSelector)
  const [isSearching, setIsSearching] = useState(false)
  const scrollRef = useRef(null)
  const hasRestoredScroll = useRef(false) // Flag to track if scroll has been restored
  const SCROLL_DELAY = 100 // milliseconds
  const {
    status,
    sort,
    loading,
    queryArchived = false
  } = useSelector((state) => state.releases)
  const { activeFilters, additionalFilterSettings } = useSelector(
    (state) => state.options
  )
  const archived = hasArchivedFilter(activeFilters)
  const isDatasetChanging =
    (archiveToFilterEnabled && archivedRedirect) ||
    Boolean(archiveToFilterEnabled && archived) !== queryArchived
  const viewStatus = isDatasetChanging ? 'loading' : status
  const dispatch = useDispatch()
  const { checkGate, isLoading: isStatsigLoading } = useStatsigClient()
  const [connectionToken] = useLocalStorage('connectionToken', '')
  const decodedToken = useMemo(
    () => decodeToken(connectionToken),
    [connectionToken]
  )

  const memoizedReleases = useMemo(() => {
    if (isDatasetChanging) return []
    if (archiveToFilterEnabled) {
      return releases.filter(
        (release) => Boolean(release.archived) === archived
      )
    }
    return releases
  }, [releases, archiveToFilterEnabled, archived, isDatasetChanging])
  const isQuickViewModalEnabled =
    !isStatsigLoading && checkGate(RELEASE_QUICKVIEW_MODAL)

  const hasReleases = memoizedReleases.length !== 0

  const onLoadMore = useCallback(async () => {
    if (loading || authLoading || isArchiveGateLoading || status === 'error')
      return
    try {
      await dispatch(
        ReleaseThunks.search(page + 1, undefined, archiveToFilterEnabled)
      )
    } catch (error) {
      console.error('error', error)
    }
  }, [
    page,
    loading,
    authLoading,
    isArchiveGateLoading,
    archiveToFilterEnabled,
    status
  ])

  useEffect(() => {
    if (authLoading || isArchiveGateLoading) return
    if (archivedRedirect) {
      if (archiveToFilterEnabled && !archived) {
        dispatch(
          OptionActions.setFilters([
            ...activeFilters,
            createArchivedFilter(t('home.archived-releases'))
          ])
        )
      }
      const nextParams = new URLSearchParams(searchParams)
      nextParams.delete('archived')
      setSearchParams(nextParams, { replace: true })
      return
    }
    if (!archiveToFilterEnabled && archived) {
      dispatch(
        OptionActions.setFilters(
          activeFilters.filter((item) => !isArchivedFilter(item))
        )
      )
      return
    }
    dispatch(ReleaseActions.setReleaseGalleryScrollPosition(0))
    if (scrollRef.current) scrollRef.current.scrollTop = 0
    dispatch(ReleaseThunks.search(1, undefined, archiveToFilterEnabled))
  }, [
    activeFilters,
    sort,
    additionalFilterSettings?.explicit,
    authLoading,
    isArchiveGateLoading,
    archiveToFilterEnabled,
    archivedRedirect
  ])

  useLayoutEffect(() => {
    if (!scrollRef.current) {
      return undefined
    }

    const scrollElement = scrollRef.current

    const handleScroll = () => {
      dispatch(
        ReleaseActions.setReleaseGalleryScrollPosition(scrollElement.scrollTop)
      )
    }

    scrollElement.addEventListener('scroll', handleScroll)

    return () => {
      scrollElement.removeEventListener('scroll', handleScroll)
    }
  }, [dispatch])

  useEffect(() => {
    // This useEffect is used to automatically scroll the component to a specific position after it mounts.
    // Only restore scroll position on initial mount, not during normal scrolling
    if (scrollRef.current && !hasRestoredScroll.current && scrollPosition > 0) {
      const timeoutId = setTimeout(() => {
        if (scrollRef.current) {
          scrollRef.current.scrollTo({
            top: scrollPosition,
            left: 0,
            behavior: 'smooth'
          })
          hasRestoredScroll.current = true // Mark as restored to prevent future restorations
        }
      }, SCROLL_DELAY)

      return () => clearTimeout(timeoutId)
    }

    return undefined
  }, [scrollPosition])

  // Reset the scroll restoration flag when filters or sort change (new search)
  useEffect(() => {
    hasRestoredScroll.current = false
  }, [activeFilters, sort])

  // Preserve and restore grid scroll when toggling search UI
  const savedScrollTopRef = useRef(0)
  useEffect(() => {
    if (isSearching) {
      if (scrollRef.current) {
        savedScrollTopRef.current = scrollRef.current.scrollTop
      }

      return undefined
    }

    // restore after grid mounts
    const timeoutId = setTimeout(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollTo({
          top: savedScrollTopRef.current,
          left: 0,
          behavior: 'auto'
        })
      }
    }, 0)

    return () => clearTimeout(timeoutId)
  }, [isSearching])

  const handlePlayRelease = (release) => {
    try {
      dispatch(
        PlayQueueThunks.playRelease(release.releaseid, {
          source: PLAY_SOURCES.RELEASES,
          trigger: PLAY_TRIGGERS.TILE
        })
      )
    } catch (error) {
      console.warn('error', error)
    }
  }

  const handleDownloadRelease = (release, format) => {
    ReleasesService.getDetail(release.releaseid).then((releaseDetail) => {
      // display notification for downloading release
      dispatch(
        NotificationActions.broadcastMessage(
          release.artist + ' - ' + release.title,
          NotificationType.addDownloadQueue
        )
      )

      for (let i = 0; i < releaseDetail.data.songs.length; i++) {
        if (releaseDetail.data.songs[i].assettype === 'video') {
          dispatch(
            DownloadsThunks.addVideoToDownloadQueue(
              releaseDetail.data.songs[i],
              'releases_view_release_card'
            )
          )
        } else {
          dispatch(
            DownloadsThunks.addTrackToDownloadQueue(
              releaseDetail.data.songs[i],
              format,
              'releases_view_release_card'
            )
          )
        }
      }
    })
  }

  const handleShowModal = (release) => {
    if (isQuickViewModalEnabled) {
      dispatch(ReleaseActions.toggleReleaseInModal(release))
      return
    }

    dispatch(
      ReleaseThunks.getDetail(release.releaseid, (releaseDetail) => {
        dispatch(ReleaseActions.toggleReleaseInModal(releaseDetail))
      })
    )
  }

  const checkHasMorePages = () => {
    if (totalItemsCount === null) {
      return true
    }
    return totalItemsCount > memoizedReleases.length
  }
  const handleSearchingChange = useCallback((value) => {
    if (value) {
      setIsSearching(true)
      return
    }

    setIsSearching(false)
  }, [])

  const renderReleases = (releaseCells) => {
    if (viewStatus === 'error') {
      return (
        <div role="alert" className="p-8 text-white">
          <p>{t('error.search-error')}</p>
          <Tonality.Button
            setting="player"
            mode="dark"
            onClick={() =>
              dispatch(
                ReleaseThunks.search(
                  page === 0 ? 1 : page + 1,
                  undefined,
                  archiveToFilterEnabled
                )
              )
            }
          >
            {t('invite-connection.retry')}
          </Tonality.Button>
        </div>
      )
    }
    if (viewStatus === 'done' && !hasReleases)
      return <EmptyReleasesFilterView />

    return isListView ? (
      <ReleaseList listviewreleases={memoizedReleases} />
    ) : (
      <ReleasesGrid $responsiveBreakpointsV2={responsiveBreakpointsV2}>
        {releaseCells}
      </ReleasesGrid>
    )
  }

  const renderReleaseGrid = () => {
    const hasMorePage = checkHasMorePages()

    const releaseCells = memoizedReleases.map((item, index) => {
      return (
        <ReleaseCard
          key={`${item.releaseid}-${index}-${item.title} - ${item.artist}`}
          handlePlayRelease={handlePlayRelease}
          handleDownloadRelease={handleDownloadRelease}
          onShowModal={handleShowModal}
          release={item}
        />
      )
    })

    return (
      <div
        ref={scrollRef}
        id="scrollableDiv"
        data-cy="releases-root"
        className={[
          rvStyles.scrollableDiv,
          !isListView && rvStyles.gridScrollable,
          !isListView && isDesktop && rvStyles.desktopGridSpacing
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <InfiniteScroll
          dataLength={memoizedReleases.length}
          hasMore={hasMorePage}
          loader={<div />}
          next={onLoadMore}
          scrollableTarget="scrollableDiv"
        >
          {viewStatus === 'loading' && isListView && <ReleaseListSkeleton />}
          {viewStatus === 'loading' && !isListView && <ReleaseCardSkeleton />}

          {renderReleases(releaseCells)}
        </InfiniteScroll>
      </div>
    )
  }

  const renderContent = () => {
    return (
      <div
        className="flex-1 h-full"
        data-responsive-breakpoints={responsiveBreakpointsVersion}
        data-responsive-breakpoints-page={RESPONSIVE_BREAKPOINT_PAGES.RELEASES}
      >
        {isSearching ? (
          <RecentlySearched />
        ) : (
          <div className="px-4 h-full w-full">{renderReleaseGrid()}</div>
        )}
      </div>
    )
  }

  const triggerAcceptInvitation = useCallback(
    async (token) => {
      try {
        await dispatch(AuthThunks.acceptInvitation({ invitation_token: token }))

        dispatch(
          NotificationActions.broadcastMessage(
            `${decodedToken.data?.inviter_email} `,
            NotificationType.connectionAccepted
          )
        )
        localStorage.removeItem('connectionToken')
      } catch (error) {
        console.error('error', error)
        if (error.status === 410) {
          dispatch(
            NotificationActions.broadcastMessage(
              '',
              NotificationType.invitationAlreadyAccepted
            )
          )
        }

        if (error.status === 409) {
          dispatch(
            NotificationActions.broadcastMessage(
              '',
              NotificationType.invitationNotForUser
            )
          )
        }
      }
    },
    [decodedToken]
  )

  useEffect(() => {
    if (connectionToken) {
      triggerAcceptInvitation(connectionToken)
    }

    return () => {
      localStorage.removeItem('connectionToken')
    }
  }, [])

  return (
    // Keep the menu and app shell on the same breakpoint version until the
    // shell phase migrates their shared 1280–1439 navigation behavior.
    <ResponsiveBreakpointsProvider
      value={RESPONSIVE_BREAKPOINT_VERSIONS.LEGACY}
    >
      <ResponsiveLayout
        content={
          <ResponsiveBreakpointsProvider value={responsiveBreakpointsVersion}>
            {renderContent()}
          </ResponsiveBreakpointsProvider>
        }
        menu={<ReleasesMenu onSearchingChange={handleSearchingChange} />}
      />
    </ResponsiveBreakpointsProvider>
  )
}

const ReleasesView = () => {
  const { t } = useTranslation()
  const { isLoading } = useArchiveToFilter()

  if (isLoading) {
    return (
      <ResponsiveLayout
        content={
          <div role="status" className="p-8 text-white">
            {t('general.loading')}
          </div>
        }
      />
    )
  }

  return (
    <ResponsiveBreakpointsRolloutBoundary
      fallback={
        <ResponsiveBreakpointsProvider
          value={RESPONSIVE_BREAKPOINT_VERSIONS.LEGACY}
        >
          <ResponsiveLayout
            content={
              <div className="p-8 text-white" aria-live="polite" role="status">
                {t('general.loading')}
              </div>
            }
          />
        </ResponsiveBreakpointsProvider>
      }
      page={RESPONSIVE_BREAKPOINT_PAGES.RELEASES}
    >
      <ReleasesContent />
    </ResponsiveBreakpointsRolloutBoundary>
  )
}

export default ReleasesView
